import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { api, imgSrc, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

export default function Product() {
  const { slug } = useParams();
  const { addItem } = useStore();
  const [product, setProduct] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setProduct(undefined);
    setQty(1);
    setImgIdx(0);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    api
      .get("/products", { params: { category: product.category, limit: 5 } })
      .then((r) => setRelated(r.data.filter((p) => p.id !== product.id).slice(0, 4)))
      .catch(() => {});
  }, [product]);

  if (product === undefined) {
    return (
      <div className="grid min-h-[70vh] place-items-center pt-24">
        <p className="label-mono">Fetching from the shelf…</p>
      </div>
    );
  }

  if (product === null) {
    return (
      <div className="grid min-h-[70vh] place-items-center px-6 pt-24 text-center" data-testid="product-not-found">
        <div>
          <p className="font-display text-4xl">This piece has flown off the shelf.</p>
          <p className="mt-3 text-sm text-stone2">It may have sold — the shop keeps the rest.</p>
          <Link to="/shop" className="btn-solid mt-8" data-testid="not-found-back-btn">
            Back to the collection
          </Link>
        </div>
      </div>
    );
  }

  const soldOut = (product.stock || 0) <= 0;
  const images = product.images?.length ? product.images : [""];

  return (
    <div className="pt-24 md:pt-32" data-testid="product-page">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone2 transition-colors hover:text-terracotta" data-testid="product-back-link">
          <ArrowLeft size={14} /> All pieces
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          <div>
            <div className="relative overflow-hidden border border-line bg-white" data-testid="product-gallery-main">
              <img
                src={imgSrc(images[imgIdx])}
                alt={product.name}
                className="aspect-[4/5] w-full object-cover"
              />
              {product.compare_at_price > product.price && (
                <span className="absolute left-0 top-4 bg-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-parchment">
                  Save {inr(product.compare_at_price - product.price)}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3" data-testid="product-gallery-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`h-20 w-20 overflow-hidden border transition-colors ${i === imgIdx ? "border-ink" : "border-line hover:border-stone-400"}`}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={imgSrc(img)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="label-mono">{product.category}</p>
            <h1 className="mt-3 font-display text-5xl leading-[1.02] tracking-tight" data-testid="product-name">
              {product.name}
            </h1>
            <div className="mt-4 flex items-baseline gap-3" data-testid="product-price">
              <span className="text-2xl font-semibold">{inr(product.price)}</span>
              {product.compare_at_price > product.price && (
                <span className="text-lg text-stone-400 line-through">{inr(product.compare_at_price)}</span>
              )}
            </div>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-stone2">
              {soldOut ? "Sold out" : product.stock <= 3 ? `Only ${product.stock} left` : "In stock · ships in 2–4 days"}
            </p>

            {product.short_desc && <p className="mt-6 font-display text-xl leading-snug text-stone2">{product.short_desc}</p>}

            <div className="mt-8 flex flex-wrap items-stretch gap-4">
              <div className="flex items-center border border-stone-300" data-testid="product-qty-stepper">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3.5 transition-colors hover:bg-cream" data-testid="qty-minus" aria-label="Decrease quantity">
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-3.5 transition-colors hover:bg-cream" data-testid="qty-plus" aria-label="Increase quantity">
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={() => addItem(product, qty)}
                disabled={soldOut}
                className="btn-solid flex-1"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag size={16} strokeWidth={1.5} /> {soldOut ? "Sold out" : "Add to cart"}
              </button>
            </div>

            {product.description && (
              <div className="mt-10 border-t border-line pt-8" data-testid="product-description">
                <p className="label-mono">About this piece</p>
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-stone2">{product.description}</p>
              </div>
            )}

            <div className="mt-8 border border-line bg-cream p-6">
              <p className="label-mono">Studio notes</p>
              <p className="mt-3 text-sm leading-relaxed text-stone2">
                One-of-a-kind by nature — slight variations are the maker's signature. Packed in
                recycled craft with a hand-written note, and shipped carefully across India.
              </p>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="py-24" data-testid="related-section">
            <Reveal>
              <h2 className="font-display text-4xl tracking-tight">More from this craft</h2>
            </Reveal>
            <div className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} delay={i * 0.06} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
